import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Home, Calendar, MapPin, Clock } from "lucide-react";

interface HousingDetailsProps {
  housingType: string;
  buildYear: string;
  postcode: string;
  executionDate: string;
  onChange: (field: string, value: string) => void;
}

export const HousingDetails = ({
  housingType,
  buildYear,
  postcode,
  executionDate,
  onChange
}: HousingDetailsProps) => {
  
  const currentYear = new Date().getFullYear();
  const twoYearsAgo = new Date();
  twoYearsAgo.setFullYear(currentYear - 2);
  
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Woninggegevens</h3>
        <p className="text-muted-foreground mb-6">
          Deze gegevens helpen ons uw subsidie nauwkeurig te berekenen en lokaal advies te geven.
        </p>
      </div>

      <div className="grid gap-6">
        {/* Housing Type */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Home className="h-4 w-4" />
              Type woning
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={housingType} onValueChange={(value) => onChange('housingType', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Selecteer uw woningtype" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vrijstaand">Vrijstaande woning</SelectItem>
                <SelectItem value="2-onder-1-kap">2-onder-1-kap woning</SelectItem>
                <SelectItem value="rijtjeshuis-tussen">Rijtjeshuis (tussen)</SelectItem>
                <SelectItem value="rijtjeshuis-hoek">Rijtjeshuis (hoek)</SelectItem>
                <SelectItem value="appartement">Appartement</SelectItem>
                <SelectItem value="bungalow">Bungalow</SelectItem>
                <SelectItem value="anders">Anders</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Build Year */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Bouwjaar (optioneel)
            </CardTitle>
            <CardDescription>
              Helpt bij het bepalen van isolatiebehoeften
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Input
              type="number"
              min="1900"
              max={currentYear}
              placeholder="Bijv. 1980"
              value={buildYear}
              onChange={(e) => onChange('buildYear', e.target.value)}
            />
          </CardContent>
        </Card>

        {/* Postcode */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Postcode
            </CardTitle>
            <CardDescription>
              Voor lokaal advies en installateurs in uw regio
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Input
              placeholder="1234 AB"
              value={postcode}
              onChange={(e) => onChange('postcode', e.target.value.toUpperCase())}
              maxLength={7}
            />
          </CardContent>
        </Card>

        {/* Execution Date */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Geplande uitvoerdatum
            </CardTitle>
            <CardDescription>
              Wanneer wilt u de maatregel laten uitvoeren?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={executionDate} onValueChange={(value) => onChange('executionDate', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Selecteer uitvoerdatum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2025-q1">Q1 2025 (Jan-Mrt)</SelectItem>
                <SelectItem value="2025-q2">Q2 2025 (Apr-Jun)</SelectItem>
                <SelectItem value="2025-q3">Q3 2025 (Jul-Sep)</SelectItem>
                <SelectItem value="2025-q4">Q4 2025 (Okt-Dec)</SelectItem>
                <SelectItem value="2026">2026</SelectItem>
                <SelectItem value="later">Later</SelectItem>
                <SelectItem value="onbekend">Nog onbekend</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* 24-month rule warning */}
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-orange-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-orange-900">24-maanden regel</h4>
                <p className="text-sm text-orange-800 mt-1">
                  U moet de ISDE subsidie aanvragen binnen 24 maanden na uitvoering van de maatregel. 
                  Plan daarom uw aanvraag op tijd!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};